import React, { useState, useCallback } from 'react'
import './ProfessionalIdInput.css'

type ProfessionalIdType =
  | 'DEA'
  | 'STATE_LICENSE'
  | 'STATE_RX_ID'
  | 'TAX_ID'
  | 'SSN'
  | 'NPI'

type ProfessionalIdInputProps = {
  id: string
  type: ProfessionalIdType
  placeholder?: string
  value: string | number
  onChange: (value: string) => void
  className?: string
  destructive?: boolean
  onBlur?: () => void
  onFocus?: () => void
  label?: string
  required?: boolean
  disabled?: boolean
}

// Validation patterns for different ID types
const VALIDATION_PATTERNS = {
  DEA: /^[A-Z]{2}[0-9]{7}$/, // 2 letters followed by 7 digits
  STATE_LICENSE: /^[A-Z0-9]{4,15}$/, // Alphanumeric, 4-15 characters (varies by state)
  STATE_RX_ID: /^[A-Z0-9]{4,12}$/, // Alphanumeric, 4-12 characters (varies by state)
  TAX_ID: /^[0-9]{2}-[0-9]{7}$/, // EIN format: XX-XXXXXXX
  SSN: /^[0-9]{3}-[0-9]{2}-[0-9]{4}$/, // SSN format: XXX-XX-XXXX
  NPI: /^[0-9]{10}$/, // 10 digits exactly
}

// Max lengths for input fields
const MAX_LENGTHS = {
  DEA: 9,
  STATE_LICENSE: 15,
  STATE_RX_ID: 12,
  TAX_ID: 10,
  SSN: 11,
  NPI: 10,
}

// Validation error messages
const ERROR_MESSAGES = {
  DEA: 'DEA number must be 2 letters followed by 7 digits (e.g., AB1234567)',
  STATE_LICENSE: 'Invalid state license number format',
  STATE_RX_ID: 'Invalid state RxID format',
  TAX_ID: 'Tax ID must be in format XX-XXXXXXX',
  SSN: 'SSN must be in format XXX-XX-XXXX',
  NPI: 'NPI number must be exactly 10 digits',
}

// Format input value based on type
const formatValue = (value: string, type: ProfessionalIdType): string => {
  const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase()

  switch (type) {
    case 'DEA':
      // Format: ABXXXXXXX
      if (cleaned.length <= 2) return cleaned
      return cleaned.slice(0, 2) + cleaned.slice(2, 9)

    case 'TAX_ID':
      // Format: XX-XXXXXXX
      if (cleaned.length <= 2) return cleaned
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 9)}`

    case 'SSN':
      // Format: XXX-XX-XXXX
      const digits = cleaned.replace(/\D/g, '')
      if (digits.length <= 3) return digits
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`

    case 'STATE_LICENSE':
    case 'STATE_RX_ID':
    case 'NPI':
    default:
      return cleaned
  }
}

// Validate input value
const validateValue = (value: string, type: ProfessionalIdType): boolean => {
  if (!value.trim()) return true // Allow empty values

  const pattern = VALIDATION_PATTERNS[type]
  return pattern.test(value)
}

// Get clean value for storage (remove formatting)
const getCleanValue = (value: string, type: ProfessionalIdType): string => {
  switch (type) {
    case 'TAX_ID':
    case 'SSN':
      return value.replace(/[^0-9]/g, '')
    case 'DEA':
    case 'STATE_LICENSE':
    case 'STATE_RX_ID':
      return value.replace(/[^A-Z0-9]/g, '').toUpperCase()
    case 'NPI':
      return value.replace(/[^0-9]/g, '')
    default:
      return value.replace(/[^A-Z0-9]/g, '').toUpperCase()
  }
}

const ProfessionalIdInput: React.FC<ProfessionalIdInputProps> = ({
  id,
  type,
  placeholder,
  value,
  onChange,
  className = '',
  destructive = false,
  onBlur,
  onFocus,
  label,
  required = false,
  disabled = false,
}) => {
  const [validationError, setValidationError] = useState<string | null>(null)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (disabled) return
      const inputValue = e.target.value
      const formattedValue = formatValue(inputValue, type)

      // Check max length
      if (formattedValue.length <= MAX_LENGTHS[type]) {
        const cleanValue = getCleanValue(formattedValue, type)
        onChange(cleanValue)

        // Clear validation error when user starts typing
        if (validationError) {
          setValidationError(null)
        }
      }
    },
    [type, onChange, validationError, disabled],
  )

  const handleBlur = useCallback((): void => {
    const stringValue = String(value)
    const formattedValue = formatValue(stringValue, type)

    if (stringValue && !validateValue(formattedValue, type)) {
      setValidationError(ERROR_MESSAGES[type])
    } else {
      setValidationError(null)
    }

    if (onBlur) {
      onBlur()
    }
  }, [value, type, onBlur])

  const handleFocus = useCallback((): void => {
    if (onFocus) {
      onFocus()
    }
  }, [onFocus])

  // Display formatted value
  const displayValue = value ? formatValue(String(value), type) : ''

  // Generate placeholder if not provided
  const defaultPlaceholder =
    placeholder ||
    (() => {
      switch (type) {
        case 'DEA':
          return 'AB1234567'
        case 'STATE_LICENSE':
          return 'Enter license number'
        case 'STATE_RX_ID':
          return 'Enter RxID'
        case 'TAX_ID':
          return '12-3456789'
        case 'SSN':
          return '123-45-6789'
        case 'NPI':
          return '1234567890'
        default:
          return ''
      }
    })()

  return (
    <div className="professional-id-input-container">
      {label && (
        <label htmlFor={id} className="professional-id-label">
          {label}
          {required && <span className="required-asterisk"> *</span>}
        </label>
      )}
      <input
        className={`professional-id-input ${className} ${
          destructive || validationError ? 'input-error' : ''
        }`}
        id={id}
        placeholder={defaultPlaceholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={disabled}
        inputMode={
          type === 'DEA' || type === 'STATE_LICENSE' || type === 'STATE_RX_ID'
            ? 'text'
            : 'numeric'
        }
        maxLength={MAX_LENGTHS[type]}
        type="text"
        autoComplete="off"
        data-testid={`professional-id-${type.toLowerCase()}`}
      />
      {validationError && (
        <div className="professional-id-error" role="alert">
          {validationError}
        </div>
      )}
    </div>
  )
}

export default ProfessionalIdInput
